"use client";

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Dialog, IconButton, TextField } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import MapComponent from "../MapComponent";
import { useToastContext } from "@/providers/toastProvider";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import { useCurrentLocation } from "../../../_hooks/useCurrentLocation";
import Geocoder from "../Geocoder";
import { useDpStore } from "@/store/dpStore";
import { pushGtmEvent } from "@/lib/utils/gtmEventHandler";
import { usePlacesAutocomplete } from "../../../_hooks/usePlacesAutocomplete";
import { formatPlacePrediction } from "@/lib/utils/places-utils";
import { fetchPlaceDetails } from "@/lib/utils/places-api";

// Places API (New) addressComponents use { longText, shortText, types }
// (vs. the classic Geocoder's { long_name, short_name, types }) — extracted
// separately here for the search-suggestion path, alongside the lat/lng and
// displayName (place name) Places API already returns for it. The drag-pin
// reverse-geocode path (Geocoder.js) has no equivalent "name" field.
function extractPlaceAddressComponents(placeDetails, position) {
  const addressComponents = placeDetails?.addressComponents || [];
  const find = type => addressComponents.find(c => c.types?.includes(type));
  return {
    name: placeDetails?.displayName?.text || null,
    locality: find("locality")?.longText || find("sublocality")?.longText || null,
    city: find("locality")?.longText || find("administrative_area_level_2")?.longText || null,
    state: find("administrative_area_level_1")?.longText || null,
    stateCode: find("administrative_area_level_1")?.shortText || null,
    pincode: find("postal_code")?.longText || null,
    country: find("country")?.longText || null,
    lat: position?.lat ?? null,
    lng: position?.lng ?? null
  };
}
const MapModal = ({
  digitalProfile
}) => {
  const {
    showSnackbar
  } = useToastContext();
  const {
    location: currentLocation,
    error: locationError,
    isLoading: locationLoading
  } = useCurrentLocation();
  const {
    isMapModalOpen,
    setIsMapModalOpen,
    enquiryFormData,
    setEnquiryFormData
  } = useDpStore();
  const eventPayload = {
    homeowner_contact_number: enquiryFormData.whatsappNumber,
    homeowner_site_address: enquiryFormData.siteLocation,
    pincode: typeof window !== "undefined" ? localStorage.getItem("multiplyonboarding_pincode") : "",
    contractor_name: digitalProfile?.profileName,
    contractor_id: digitalProfile?.contractorId,
    contractor_phone_number: digitalProfile?.phoneNumber,
    contractor_profile_id: digitalProfile?.profileName
  };
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedAddressComponents, setSelectedAddressComponents] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [dropdownDismissed, setDropdownDismissed] = useState(false);
  const hasNotifiedRef = useRef(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  // When address is set from dropdown, ignore the next Geocoder result so dropdown address is kept
  const skipNextAddressUpdateRef = useRef(false);
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  const trimmedSearchQuery = searchQuery?.trim() || "";
  const {
    predictions,
    isLoading: searchLoading
  } = usePlacesAutocomplete(trimmedSearchQuery, {
    debounceMs: 300
  });
  const formattedPredictions = useMemo(() => predictions.map(p => formatPlacePrediction(p)), [predictions]);
  const filteredPredictions = useMemo(() => formattedPredictions.filter(loc => loc.primary || loc.fullAddress), [formattedPredictions]);
  const hasPredictions = filteredPredictions.length > 0;
  const showSearchDropdown = trimmedSearchQuery.length > 0 && (searchFocused || hasPredictions && !dropdownDismissed);

  // Close dropdown when clicking outside the search container
  useEffect(() => {
    if (!isMapModalOpen || !showSearchDropdown) return;
    const handlePointerDown = e => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setDropdownDismissed(true);
        setSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("touchstart", handlePointerDown, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("touchstart", handlePointerDown, true);
    };
  }, [isMapModalOpen, showSearchDropdown]);

  // Default location (Bangalore) coordinates - constant outside component
  const DEFAULT_LOCATION_LAT = 12.9352;
  const DEFAULT_LOCATION_LNG = 77.6245;

  // Notify user if using default location
  useEffect(() => {
    if (isMapModalOpen && !locationLoading && locationError && currentLocation && !hasNotifiedRef.current) {
      // Check if current location is the default location
      const isDefaultLocation = Math.abs(currentLocation.lat - DEFAULT_LOCATION_LAT) < 0.01 && Math.abs(currentLocation.lng - DEFAULT_LOCATION_LNG) < 0.01;
      if (isDefaultLocation) {
        showSnackbar("Unable to access your location. Showing default location. You can drag the marker to your location.", "info");
        hasNotifiedRef.current = true;
      }
    }
  }, [isMapModalOpen, locationLoading, locationError, currentLocation, showSnackbar]);

  // Reset notification flag when modal closes
  useEffect(() => {
    if (!isMapModalOpen) {
      hasNotifiedRef.current = false;
    }
  }, [isMapModalOpen]);

  // Derive the effective position: use selectedPosition if set, otherwise use currentLocation
  const effectivePosition = useMemo(() => {
    return selectedPosition || (currentLocation && !locationLoading ? currentLocation : null);
  }, [selectedPosition, currentLocation, locationLoading]);
  const handlePositionChange = useCallback(position => {
    setSelectedPosition(position);
  }, []);
  const handleAddressChange = useCallback((address, components) => {
    if (skipNextAddressUpdateRef.current) {
      skipNextAddressUpdateRef.current = false;
      return;
    }
    setSelectedAddress(address);
    setSelectedAddressComponents(components || null);
  }, []);
  const handleSelectSearchSuggestion = useCallback(async location => {
    searchInputRef.current?.blur();
    const placeId = location.placeId;
    if (!placeId) return;
    try {
      const placeDetails = await fetchPlaceDetails({
        placeId,
        fieldMask: "location,formattedAddress,addressComponents,displayName"
      });
      const lat = placeDetails.location?.latitude;
      const lng = placeDetails.location?.longitude;
      const address_0 = placeDetails.formattedAddress || location.fullAddress || `${location.primary || ""}${location.secondary ? `, ${location.secondary}` : ""}`;
      if (lat != null && lng != null) {
        const position_0 = {
          lat: Number(lat),
          lng: Number(lng)
        };
        const components_0 = extractPlaceAddressComponents(placeDetails, position_0);
        skipNextAddressUpdateRef.current = true;
        setSelectedPosition(position_0);
        setSelectedAddress(address_0);
        setSelectedAddressComponents(components_0);
        setEnquiryFormData({
          ...enquiryFormData,
          siteLocation: address_0,
          siteLocationError: false,
          siteLocationDetails: {
            addressLine1: address_0,
            ...components_0
          }
        });
        setSearchQuery(address_0);
        setSearchFocused(false);
        setDropdownDismissed(true);
        pushGtmEvent({
          event_name: "location_suggestion_selected",
          ...eventPayload
        });
      }
    } catch (err) {
      showSnackbar("Could not load location details. Please try again.", "error");
    }
  }, [enquiryFormData, setEnquiryFormData, eventPayload, showSnackbar]);
  const handleConfirm = () => {
    if (effectivePosition && selectedAddress) {
      setEnquiryFormData({
        ...enquiryFormData,
        siteLocation: selectedAddress,
        siteLocationDetails: {
          addressLine1: selectedAddress,
          ...(selectedAddressComponents || {}),
          lat: selectedAddressComponents?.lat ?? effectivePosition.lat,
          lng: selectedAddressComponents?.lng ?? effectivePosition.lng
        }
      });
      setIsMapModalOpen(false);
      pushGtmEvent({
        event_name: "confirm_location_on_map_clicked",
        ...eventPayload
      });
    }
  };
  const handleClose = () => {
    setSelectedPosition(null);
    setSelectedAddress(null);
    setSelectedAddressComponents(null);
    setIsMapModalOpen(false);
  };
  const isLoading = locationLoading || !effectivePosition;
  const canConfirm = !isLoading && selectedAddress;

  // Error state for missing API key
  if (!apiKey) {
    return <Dialog open={isMapModalOpen} onClose={handleClose} fullScreen slotProps={{
      paper: {
        sx: {
          borderRadius: 0,
          overflow: "hidden",
          position: "relative",
          background: "transparent",
          padding: 0,
          height: "100vh",
          maxHeight: "100vh",
          display: "flex",
          flexDirection: "column"
        }
      }
    }}>
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center">
            <p className="m3-body-large mb-4" style={{
            color: "var(--textErrorError, #B3261E)"
          }}>
              Google Maps API key is not configured
            </p>
            <p className="m3-body-medium mb-4" style={{
            color: "var(--textSecondarySecondary, #5E5F59)"
          }}>
              Make sure NEXT_PUBLIC_GOOGLE_MAPS_API_KEY is set in your
              .env.local file
            </p>
            <IconButton onClick={handleClose} sx={{
            padding: "8px",
            color: "var(--textPrimaryPrimary, #1B1C17)"
          }}>
              <ArrowBackIcon />
            </IconButton>
          </div>
        </div>
      </Dialog>;
  }
  return <Dialog open={isMapModalOpen} onClose={handleClose} fullScreen slotProps={{
    paper: {
      sx: {
        borderRadius: 0,
        overflow: "visible",
        position: "relative",
        background: "white",
        padding: 0,
        height: "100%",
        maxHeight: "100%",
        display: "flex",
        flexDirection: "column"
      }
    },
    backdrop: {
      sx: {
        backdropFilter: "blur(4px)"
      }
    }
  }}>
      <APIProvider apiKey={apiKey} libraries={["marker"]}>
        {/* Back Button - Floating over map */}
        <IconButton onClick={handleClose} sx={{
        position: "absolute",
        top: 54,
        left: 16,
        zIndex: 1100,
        backgroundColor: "white",
        color: "var(--textSecondarySecondary, #5E5F59)",
        padding: "12px",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.9)"
        }
      }}>
          <ArrowBackIcon />
        </IconButton>

        {/* Search bar with dropdown */}
        <div ref={searchContainerRef} className="absolute top-12 left-0 right-0 z-[1000] px-4 pb-2" style={{
        paddingLeft: "74px"
      }}>
          <div className="relative bg-white rounded-xl shadow-md" style={{
          border: "1px solid var(--colorSecondarySecondaryContainer, #DEE1F9)",
          overflow: "visible"
        }}>
            <TextField inputRef={searchInputRef} fullWidth placeholder="Search for a location" value={searchQuery} onChange={e_0 => {
            setSearchQuery(e_0.target.value);
            setDropdownDismissed(false);
          }} onFocus={() => {
            setSearchFocused(true);
            setDropdownDismissed(false);
          }} onBlur={() => setTimeout(() => setSearchFocused(false), 200)} variant="standard" slotProps={{
            input: {
              disableUnderline: true,
              sx: {
                padding: "12px 16px",
                paddingRight: searchQuery ? "8px" : "16px",
                fontSize: "16px",
                color: "var(--textPrimaryPrimary, #1B1C17)"
              },
              endAdornment: searchQuery ? <IconButton size="small" onClick={() => {
                setSearchQuery("");
                setDropdownDismissed(false);
                searchInputRef.current?.focus();
              }} sx={{
                padding: "4px"
              }} aria-label="Clear search">
                      <CloseIcon fontSize="small" />
                    </IconButton> : null
            }
          }} />
            {showSearchDropdown && <ul className="absolute left-0 right-0 top-full z-10 max-h-[280px] overflow-auto bg-white border-t list-none m-0 p-0 rounded-b-xl" style={{
            borderColor: "var(--colorSecondarySecondaryContainer, #DEE1F9)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
          }}>
                {searchLoading ? <li className="px-4 py-4 text-center m3-body-medium" style={{
              color: "var(--textSecondarySecondary, #5E5F59)"
            }}>
                    Searching...
                  </li> : hasPredictions ? filteredPredictions.map((loc_0, index) => <li key={loc_0.placeId || `search-${index}`} role="button" tabIndex={0} onMouseDown={e_1 => {
              e_1.preventDefault();
              handleSelectSearchSuggestion(loc_0);
            }} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[rgba(0,75,238,0.04)]" style={{
              borderBottom: index < filteredPredictions.length - 1 ? "1px solid var(--colorSecondarySecondaryContainer, #DEE1F9)" : "none"
            }}>
                        <LocationOnRoundedIcon sx={{
                color: "var(--colorPrimaryPrimary, #004BEE)",
                fontSize: 20
              }} />
                        <div className="flex-1 min-w-0">
                          <p className="m3-body-large truncate" style={{
                  color: "var(--textPrimaryPrimary, #1B1C17)"
                }}>
                            {loc_0.primary || loc_0.fullAddress}
                          </p>
                          {loc_0.secondary && <p className="m3-body-medium truncate" style={{
                  color: "var(--textSecondarySecondary, #5E5F59)"
                }}>
                              {loc_0.secondary}
                            </p>}
                        </div>
                      </li>) : <li className="px-4 py-4 text-center m3-body-medium" style={{
              color: "var(--textSecondarySecondary, #5E5F59)"
            }}>
                    No locations found
                  </li>}
              </ul>}
          </div>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative w-full" style={{
        height: "calc(100vh - 180px)",
        minHeight: "500px"
      }}>
          {isLoading && <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="m3-body-medium" style={{
              color: "var(--textSecondarySecondary, #5E5F59)"
            }}>
                  Loading map...
                </p>
              </div>
            </div>}

          {(currentLocation || selectedPosition) && !isLoading && <div className="absolute inset-0 w-full h-full">
              <MapComponent center={effectivePosition} onPositionChange={handlePositionChange} />
              {effectivePosition && <Geocoder position={effectivePosition} onAddressChange={handleAddressChange} />}
            </div>}

          {/* {locationError && (
            <div
              className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1101] px-4 py-2 rounded text-sm"
              style={{
                backgroundColor: "var(--colorErrorErrorContainer, #F9DEDC)",
                border: "1px solid var(--colorErrorError, #B3261E)",
                color: "var(--textErrorError, #B3261E)",
              }}
            >
              {locationError} - Using default location
            </div>
           )} */}
        </div>

        {/* Bottom Panel */}
        <div className="bg-white border-t p-4" style={{
        borderTop: "1px solid var(--colorSecondarySecondaryContainer, #DEE1F9)"
      }}>
          <div className="mb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex justify-center items-center ">
                <LocationOnRoundedIcon sx={{
                color: "var(--colorPrimaryPrimary, #004BEE)"
              }} />
              </div>
              <div className="flex-1">
                {selectedAddress ? <div>
                    <p className="m3-body-large mb-1" style={{
                  color: "var(--textPrimaryPrimary, #1B1C17)",
                  fontWeight: 600
                }}>
                      Selected Location
                    </p>
                    <p className="m3-body-medium" style={{
                  color: "var(--textSecondarySecondary, #5E5F59)"
                }}>
                      {selectedAddress}
                    </p>
                  </div> : <p className="m3-body-medium" style={{
                color: "var(--textSecondarySecondary, #5E5F59)"
              }}>
                    Loading address...
                  </p>}
              </div>
            </div>
          </div>
          <button onClick={() => {
          handleConfirm();
        }} disabled={!canConfirm} className="m3-label-large w-full py-3 px-6 rounded-lg transition-colors" style={{
          backgroundColor: canConfirm ? "var(--colorPrimaryPrimary, #004BEE)" : "var(--colorDisabledDisabled, #17191C59)",
          color: canConfirm ? "var(--textPrimaryOnColorPrimary, #F2F1E9)" : "var(--textDisabledDisabled, #17191C59)",
          border: "none",
          cursor: canConfirm ? "pointer" : "not-allowed",
          textTransform: "none"
        }}>
            Confirm Location
          </button>
        </div>
      </APIProvider>
    </Dialog>;
};
export default MapModal;